import * as React from 'react';

import type { CourseAggregate, Session } from '@/features/modules/types';
import type { ProfilesResponse } from '@/features/user/types/get-profiles';
import {
  shouldShowIMOEnrollment,
  shouldShowMathleteRegistration,
  shouldShowNaplanEnrollment,
  shouldShowSchoolCurriculumEnrollment,
} from '@/features/user-notifications/config/enrollment-eligibility';

import { IMOCard } from '../components/imo/imo-card';
import { MathleteRegistrationCard } from '../components/mathlete-registration/mathlete-registration-card';
import { NaplanCard } from '../components/naplan/naplan-card';
import { OrientationCard } from '../components/orientation/orientation-card';
import { SchoolCurriculumCard } from '../components/school-curriculum/school-curriculum-card';

/**
 * Data required to determine if a notification should be shown
 */
export interface NotificationData {
  courseAggregate?: CourseAggregate;
  enrolledCourses?: ProfilesResponse.EnrolledCourse[];
  selectedProfileId?: string;
  selectedVariantId?: string;
  country?: string;
  allSessions?: Session[];
}

/**
 * Configuration for a notification type
 */
export interface NotificationConfig {
  /**
   * Unique identifier for this notification
   */
  id: string;

  /**
   * Function to determine if this notification should be shown
   */
  isEnabled: (data: NotificationData) => boolean;

  /**
   * Function to render the notification card component
   * The card is self-contained and handles its own interactions
   * @param key - Optional key for React rendering optimization
   */
  getCard: (key?: string) => React.ReactElement;
}

/**
 * Array of all notification configurations (internal use only)
 * Notifications are evaluated in order, and all enabled notifications will be shown
 */
const NOTIFICATION_CONFIGS: NotificationConfig[] = [
  {
    id: 'dashboard-orientation',
    isEnabled: () => {
      // Always show dashboard orientation card
      return true;
    },

    getCard: (key?: string) => {
      return <OrientationCard key={key} />;
    },
  },
  {
    id: 'school-curriculum',
    isEnabled: (data: NotificationData) => {
      return shouldShowSchoolCurriculumEnrollment(
        data.courseAggregate,
        data.enrolledCourses
      );
    },

    getCard: (key?: string) => {
      return <SchoolCurriculumCard key={key} />;
    },
  },
  {
    id: 'naplan',
    isEnabled: (data: NotificationData) => {
      return shouldShowNaplanEnrollment(
        data.courseAggregate,
        data.enrolledCourses,
        data.country,
        data.allSessions
      );
    },

    getCard: (key?: string) => {
      return <NaplanCard key={key} />;
    },
  },
  {
    id: 'imo',
    isEnabled: (data: NotificationData) => {
      return shouldShowIMOEnrollment(
        data.courseAggregate,
        data.enrolledCourses,
        data.selectedVariantId
      );
    },

    getCard: (key?: string) => {
      return <IMOCard key={key} />;
    },
  },
  {
    id: 'mathlete-registration',
    isEnabled: (data: NotificationData) => {
      return shouldShowMathleteRegistration(data.enrolledCourses);
    },

    getCard: (key?: string) => {
      return <MathleteRegistrationCard key={key} />;
    },
  },
];

/**
 * Get all enabled notifications based on the provided data
 */
export const getEnabledNotifications = (
  data: NotificationData
): NotificationConfig[] => {
  return NOTIFICATION_CONFIGS.filter(config => config.isEnabled(data));
};
